import { Camera, Close } from '@carbon/icons-react';
import { CustomizedTypography } from '@components/Customized';
import { colorSet, useSnackbar } from '@components/Provider';
import useFiles, { UploadedFileReturnType } from '@hooks/useFiles';
import { Box, Grid, IconButton } from '@material-ui/core';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import useStyles from './styles';
import { DefaultThumbnail } from '@assets';
import { Controller, useFormContext } from 'react-hook-form';
import { RfqReview } from '@modules/review';
import { getFileUrl } from '@utils/urls';
import { useSelector } from '@store';

const QUANTITY_LIMIT = 20;
const SIZE_LIMIT_MB = 10;

const AttachPicturesBox = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { onUploadFiles } = useFiles();

  const review = useSelector((state) => state.review);

  const writtenReviewFiles = useMemo(() => review.reviewFiles ?? [], [
    review.reviewFiles
  ]);

  const { control, setValue } = useFormContext<RfqReview>();

  const [uploadedFiles, setUploadedFile] = useState<UploadedFileReturnType[]>(
    []
  );

  const [disabled, setDiasbled] = useState<boolean>(false);

  useEffect(() => {
    if (writtenReviewFiles.length > 0) {
      setUploadedFile((prev) => writtenReviewFiles);
    }
  }, [writtenReviewFiles]);

  useEffect(() => {
    uploadedFiles && uploadedFiles.length >= 20
      ? setDiasbled(true)
      : setDiasbled(false);

    const reviewFiles = uploadedFiles.map(({ fileId, fileName }) => ({
      fileId,
      fileName
    }));

    setValue('reviewFiles', reviewFiles);

    //eslint-disable-next-line
  }, [uploadedFiles]);

  const onClickDeleteFile = useCallback((fileId: string) => {
    setUploadedFile((prev) => prev.filter((file) => file.fileId !== fileId));
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    accept: '.jpg, .jpeg, .png',
    onDrop: async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const overSizeFiles = acceptedFiles.filter(
        (file) => file.size > SIZE_LIMIT_MB * 1024 * 1024
      ).length;

      if (overSizeFiles > 0) {
        return enqueueSnackbar(
          `사진 파일은 1개당 ${SIZE_LIMIT_MB}MB 이하의 파일만 업로드 가능합니다.`,
          { variant: 'error' }
        );
      }

      if (uploadedFiles.length + acceptedFiles.length > QUANTITY_LIMIT) {
        return enqueueSnackbar(
          `사진 파일은 ${QUANTITY_LIMIT}개까지 업로드 가능합니다.`,
          { variant: 'error' }
        );
      }

      const res = await onUploadFiles(acceptedFiles);

      if (res.error) {
        return enqueueSnackbar(`파일 업로드 실패`, { variant: 'error' });
      }

      if (!res.error) {
        setUploadedFile((prev) => [...prev, ...res.files]);

        return enqueueSnackbar('파일 업로드 완료', { variant: 'success' });
      }
    },
    onDropRejected: () => {
      enqueueSnackbar('지원하지 않는 형식입니다.');
    }
  });

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      justifyContent="center"
      className={classes.attachPictures}
      {...getRootProps()}
    >
      <Controller
        name="reviewFiles"
        defaultValue=""
        control={control}
        render={() => (
          <input
            {...getInputProps()}
            hidden
            accept="image/*"
            multiple
            type="file"
            name="reviewFiles"
          />
        )}
      />

      <Box className="photoBox" onClick={() => !disabled && open()}>
        <Camera
          size={20}
          color={disabled ? colorSet.gray500 : colorSet.gray2000}
        />
        <CustomizedTypography
          variant="body1"
          color={disabled ? 'textSecondary' : 'textPrimary'}
          fontWeight="500"
        >
          제품 사진 첨부하기 ({uploadedFiles && uploadedFiles.length}/20)
        </CustomizedTypography>
      </Box>
      <CustomizedTypography variant="caption" color="textSecondary">
        거래와 무관한 사진을 첨부한 경우 통보없이 삭제될 수 있습니다.
      </CustomizedTypography>
      {uploadedFiles.length > 0 ? (
        <Grid container className={classes.imageContainer}>
          {uploadedFiles.map((file, index) => {
            const { fileId, fileName } = file;

            return (
              <Box className="imageBox" key={fileId}>
                <img
                  src={getFileUrl(fileId)}
                  alt={fileName}
                  width="80"
                  height="80"
                  onError={(e) => {
                    e.currentTarget.src = DefaultThumbnail;
                  }}
                />
                <IconButton
                  onClick={() => onClickDeleteFile(fileId)}
                  data-cy={`reviewFiles-delete-${fileId}`}
                  className="deleteButton"
                >
                  <Close size={24} color={colorSet.gray000} />
                </IconButton>
              </Box>
            );
          })}
        </Grid>
      ) : null}
    </Grid>
  );
};

export default AttachPicturesBox;
